import React, { useState } from 'react';
import { SearchIcon, FilterIcon, WifiIcon, WifiOffIcon, AlertTriangleIcon, ActivityIcon, MapPinIcon, GaugeIcon, ZapIcon, XIcon, InfoIcon, CheckCircle2Icon, ClockIcon, UserIcon, LayoutGridIcon, ListIcon } from 'lucide-react';
import { useMavisData } from '../hooks/useMavisData';
import { LineChart, Line, ResponsiveContainer, YAxis, AreaChart, Area, XAxis, CartesianGrid, Tooltip } from 'recharts';
export function SmartMeters() {
  const data = useMavisData();
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('All');
  const [selectedMeterId, setSelectedMeterId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const filteredMeters = data.smartMeters.filter(meter => {
    const matchesSearch = meter.id.includes(searchTerm) || meter.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filter === 'All' || meter.status === filter;
    return matchesSearch && matchesFilter;
  });
  const selectedMeter = data.smartMeters.find(m => m.id === selectedMeterId);
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Online':
        return <WifiIcon className="w-4 h-4 text-mavis-green" data-id="element-2258" />;
      case 'Offline':
        return <WifiOffIcon className="w-4 h-4 text-red-500" data-id="element-2259" />;
      case 'Warning':
        return <AlertTriangleIcon className="w-4 h-4 text-mavis-orange" data-id="element-2260" />;
      default:
        return <WifiIcon className="w-4 h-4 text-gray-400" data-id="element-2261" />;
    }
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Online':
        return 'bg-mavis-green/10 text-mavis-green border-mavis-green/20';
      case 'Offline':
        return 'bg-red-50 text-red-600 border-red-100';
      case 'Warning':
        return 'bg-mavis-orange/10 text-mavis-orange border-mavis-orange/20';
      default:
        return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };
  const daysOfWeek = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  return <div className="p-8 max-w-7xl mx-auto animate-in fade-in duration-500" data-id="element-2262">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-8 gap-4" data-id="element-2263">
        <div data-id="element-2264">
          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-2265">
            Smart Meter Network
          </h1>
          <p className="text-gray-500" data-id="element-2266">
            Monitor real-time consumption and status of municipal meters.
          </p>
        </div>

        <div className="flex gap-3 w-full md:w-auto" data-id="element-2267">
          <div className="relative flex-1 md:w-64" data-id="element-2268">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-2269" />
            <input type="text" placeholder="Search meter or name..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-none focus:ring-mavis-teal focus:border-mavis-teal text-sm" data-id="element-2270" />

          </div>
          <div className="relative" data-id="element-2271">
            <select value={filter} onChange={e => setFilter(e.target.value)} className="appearance-none pl-10 pr-8 py-2 border border-gray-300 rounded-none focus:ring-mavis-teal focus:border-mavis-teal text-sm bg-white" data-id="element-2272">

              <option value="All" data-id="element-2273">All Status</option>
              <option value="Online" data-id="element-2274">Online</option>
              <option value="Offline" data-id="element-2275">Offline</option>
              <option value="Warning" data-id="element-2276">Warning</option>
            </select>
            <FilterIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-2277" />
          </div>
          <div className="flex border border-gray-300" data-id="element-2278">
            <button onClick={() => setViewMode('grid')} className={`${`p-2 transition-colors ${viewMode === 'grid' ? 'bg-mavis-teal text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`} bg-[#002F3E]`} title="Grid view" data-id="element-2279">

              <LayoutGridIcon className="w-4 h-4" data-id="element-2280" />
            </button>
            <button onClick={() => setViewMode('table')} className={`p-2 transition-colors border-l border-gray-300 ${viewMode === 'table' ? 'bg-mavis-teal text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`} title="Table view" data-id="element-2281">

              <ListIcon className="w-4 h-4" data-id="element-2282" />
            </button>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-id="element-2283">
        <div className="bg-white p-4 rounded-none border border-gray-100 shadow-sm flex items-center gap-4" data-id="element-2284">
          <div className="p-3 bg-blue-50 text-blue-600 rounded-none" data-id="element-2285">
            <ActivityIcon className="w-5 h-5" data-id="element-2286" />
          </div>
          <div data-id="element-2287">
            <p className="text-xs text-gray-500 font-medium uppercase" data-id="element-2288">
              Total Meters
            </p>
            <p className="text-xl font-bold text-gray-900 font-mono" data-id="element-2289">24,592</p>
          </div>
        </div>
        <div className="bg-white p-4 rounded-none border border-gray-100 shadow-sm flex items-center gap-4" data-id="element-2290">
          <div className="p-3 bg-mavis-green/10 text-mavis-green rounded-none" data-id="element-2291">
            <WifiIcon className="w-5 h-5" data-id="element-2292" />
          </div>
          <div data-id="element-2293">
            <p className="text-xs text-gray-500 font-medium uppercase" data-id="element-2294">
              Online Rate
            </p>
            <p className="text-xl font-bold text-gray-900 font-mono" data-id="element-2295">94.2%</p>
          </div>
        </div>
        <div className="bg-white p-4 rounded-none border border-gray-100 shadow-sm flex items-center gap-4" data-id="element-2296">
          <div className="p-3 bg-mavis-orange/10 text-mavis-orange rounded-none" data-id="element-2297">
            <AlertTriangleIcon className="w-5 h-5" data-id="element-2298" />
          </div>
          <div data-id="element-2299">
            <p className="text-xs text-gray-500 font-medium uppercase" data-id="element-2300">
              Active Alerts
            </p>
            <p className="text-xl font-bold text-gray-900 font-mono" data-id="element-2301">143</p>
          </div>
        </div>
        <div className="bg-white p-4 rounded-none border border-gray-100 shadow-sm flex items-center gap-4" data-id="element-2302">
          <div className="p-3 bg-purple-50 text-purple-600 rounded-none" data-id="element-2303">
            <ZapIcon className="w-5 h-5" data-id="element-2304" />
          </div>
          <div data-id="element-2305">
            <p className="text-xs text-gray-500 font-medium uppercase" data-id="element-2306">
              Avg Daily Cons.
            </p>
            <p className="text-xl font-bold text-gray-900 font-mono" data-id="element-2307">
              14.2 <span className="text-sm font-sans text-gray-500" data-id="element-2308">kWh</span>
            </p>
          </div>
        </div>
      </div>

      {/* Table View */}
      {viewMode === 'table' && <div className="bg-white rounded-none shadow-sm border border-gray-200 overflow-hidden" data-id="element-2309">
          <div className="overflow-x-auto" data-id="element-2310">
            <table className="w-full text-left border-collapse" data-id="element-2311">
              <thead data-id="element-2312">
                <tr className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider border-b border-gray-200" data-id="element-2313">
                  <th className="p-4 font-medium" data-id="element-2314">Status</th>
                  <th className="p-4 font-medium" data-id="element-2315">Meter ID</th>
                  <th className="p-4 font-medium" data-id="element-2316">Customer</th>
                  <th className="p-4 font-medium" data-id="element-2317">Address</th>
                  <th className="p-4 font-medium text-right" data-id="element-2318">Reading (kWh)</th>
                  <th className="p-4 font-medium text-right" data-id="element-2319">Daily (kWh)</th>
                  <th className="p-4 font-medium" data-id="element-2320">Last Comm</th>
                  <th className="p-4 font-medium w-20" data-id="element-2321">Trend</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100" data-id="element-2322">
                {filteredMeters.map(meter => <tr key={meter.id} onClick={() => setSelectedMeterId(meter.id)} className="hover:bg-gray-50 transition-colors group cursor-pointer" data-id="element-2323">

                    <td className="p-4" data-id="element-2324">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-none text-xs font-medium border ${getStatusColor(meter.status)}`} data-id="element-2325">

                        {getStatusIcon(meter.status)}
                        {meter.status}
                      </span>
                    </td>
                    <td className="p-4" data-id="element-2326">
                      <span className="font-mono font-bold text-gray-900" data-id="element-2327">
                        {meter.id}
                      </span>
                    </td>
                    <td className="p-4" data-id="element-2328">
                      <span className="text-sm font-medium text-gray-700" data-id="element-2329">
                        {meter.name}
                      </span>
                    </td>
                    <td className="p-4" data-id="element-2330">
                      <span className="text-sm text-gray-500 flex items-center gap-1" data-id="element-2331">
                        <MapPinIcon className="w-3 h-3 shrink-0" data-id="element-2332" />
                        <span className="truncate max-w-[200px]" data-id="element-2333">
                          {meter.address}
                        </span>
                      </span>
                    </td>
                    <td className="p-4 text-right" data-id="element-2334">
                      <span className="font-mono font-bold text-mavis-teal" data-id="element-2335">
                        {meter.reading.toFixed(1)}
                      </span>
                    </td>
                    <td className="p-4 text-right" data-id="element-2336">
                      <span className="font-mono font-medium text-gray-900" data-id="element-2337">
                        {meter.daily.toFixed(1)}
                      </span>
                    </td>
                    <td className="p-4" data-id="element-2338">
                      <span className="text-xs text-gray-500" data-id="element-2339">
                        {meter.lastComm}
                      </span>
                    </td>
                    <td className="p-4" data-id="element-2340">
                      <div className="h-6 w-16 opacity-70 group-hover:opacity-100 transition-opacity" data-id="element-2341">
                        <ResponsiveContainer width="100%" height="100%" data-id="element-2342">
                          <LineChart data={meter.history.map((val, i) => ({
                      val,
                      i
                    }))} data-id="element-2343">

                            <YAxis domain={['dataMin - 2', 'dataMax + 2']} hide data-id="element-2344" />


                            <Line type="monotone" dataKey="val" stroke={meter.status === 'Offline' ? '#ef4444' : '#0D4F5C'} strokeWidth={2} dot={false} isAnimationActive={false} data-id="element-2345" />

                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
          {filteredMeters.length === 0 && <div className="text-center py-12" data-id="element-2346">
              <GaugeIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" data-id="element-2347" />
              <h3 className="text-lg font-medium text-gray-900" data-id="element-2348">
                No meters found
              </h3>
              <p className="text-gray-500" data-id="element-2349">
                Try adjusting your search or filters.
              </p>
            </div>}
        </div>}

      {/* Grid View (existing Meter Grid) */}
      {viewMode === 'grid' && <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-2350">
            {filteredMeters.map(meter => <div key={meter.id} onClick={() => setSelectedMeterId(meter.id)} className="bg-white rounded-none border border-gray-200 shadow-sm hover:shadow-md transition-all overflow-hidden group cursor-pointer hover:border-mavis-teal/30" data-id="element-2351">

                <div className="p-5 border-b border-gray-100" data-id="element-2352">
                  <div className="flex justify-between items-start mb-3" data-id="element-2353">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-none text-xs font-medium border ${getStatusColor(meter.status)}`} data-id="element-2354">

                      {getStatusIcon(meter.status)}
                      {meter.status}
                    </span>
                    <span className="text-xs text-gray-400" data-id="element-2355">
                      Last: {meter.lastComm}
                    </span>
                  </div>
                  <h3 className="font-mono font-bold text-lg text-gray-900 mb-1" data-id="element-2356">
                    {meter.id}
                  </h3>
                  <p className="text-sm font-medium text-gray-700" data-id="element-2357">
                    {meter.name}
                  </p>
                  <p className="text-xs text-gray-500 flex items-center gap-1 mt-1 truncate" data-id="element-2358">
                    <MapPinIcon className="w-3 h-3 shrink-0" data-id="element-2359" /> {meter.address}
                  </p>
                </div>

                <div className="p-5 bg-gray-50/50 grid grid-cols-2 gap-4" data-id="element-2360">
                  <div data-id="element-2361">
                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-2362">
                      Total Reading
                    </p>
                    <p className="font-mono font-bold text-mavis-teal text-lg" data-id="element-2363">
                      {meter.reading.toFixed(1)}{' '}
                      <span className="text-xs font-sans text-gray-500 font-normal" data-id="element-2364">
                        kWh
                      </span>
                    </p>
                  </div>
                  <div data-id="element-2365">
                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-2366">
                      Today's Usage
                    </p>
                    <div className="flex items-end gap-2" data-id="element-2367">
                      <p className="font-mono font-bold text-gray-900 text-lg" data-id="element-2368">
                        {meter.daily.toFixed(1)}{' '}
                        <span className="text-xs font-sans text-gray-500 font-normal" data-id="element-2369">
                          kWh
                        </span>
                      </p>
                      {/* Mini Sparkline */}
                      <div className="h-6 w-16 mb-1 opacity-70 group-hover:opacity-100 transition-opacity" data-id="element-2370">
                        <ResponsiveContainer width="100%" height="100%" data-id="element-2371">
                          <LineChart data={meter.history.map((val, i) => ({
                      val,
                      i
                    }))} data-id="element-2372">

                            <YAxis domain={['dataMin - 2', 'dataMax + 2']} hide data-id="element-2373" />


                            <Line type="monotone" dataKey="val" stroke={meter.status === 'Offline' ? '#ef4444' : '#0D4F5C'} strokeWidth={2} dot={false} isAnimationActive={false} data-id="element-2374" />

                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                </div>
              </div>)}
          </div>

          {filteredMeters.length === 0 && <div className="text-center py-12 bg-white rounded-none border border-gray-200 border-dashed" data-id="element-2375">
              <GaugeIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" data-id="element-2376" />
              <h3 className="text-lg font-medium text-gray-900" data-id="element-2377">
                No meters found
              </h3>
              <p className="text-gray-500" data-id="element-2378">
                Try adjusting your search or filters.
              </p>
            </div>}
        </>}

      {/* Detail Panel Backdrop */}
      {selectedMeterId && <div className="fixed inset-0 bg-black/50 z-40 transition-opacity" onClick={() => setSelectedMeterId(null)} data-id="element-2379" />}

      {/* Detail Panel */}
      <div className={`fixed right-0 top-0 h-full w-full max-w-[450px] bg-white z-50 shadow-2xl border-l border-gray-200 transform transition-transform duration-300 ease-in-out overflow-y-auto ${selectedMeterId ? 'translate-x-0' : 'translate-x-full'}`} data-id="element-2380">

        {selectedMeter && <div className="flex flex-col h-full" data-id="element-2381">
            {/* Header */}
            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50" data-id="element-2382">
              <div data-id="element-2383">
                <h2 className="text-2xl font-bold font-mono text-gray-900" data-id="element-2384">
                  {selectedMeter.id}
                </h2>
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-none text-xs font-medium border mt-2 ${getStatusColor(selectedMeter.status)}`} data-id="element-2385">

                  {getStatusIcon(selectedMeter.status)}
                  {selectedMeter.status}
                </span>
              </div>
              <button onClick={() => setSelectedMeterId(null)} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-none transition-colors" data-id="element-2386">

                <XIcon className="w-6 h-6" data-id="element-2387" />
              </button>
            </div>

            <div className="p-6 space-y-8 flex-1" data-id="element-2388">
              {/* Customer Info */}
              <section data-id="element-2389">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4 border-b border-gray-100 pb-2" data-id="element-2390">
                  Customer Details
                </h3>
                <div className="space-y-3" data-id="element-2391">
                  <div className="flex items-start gap-3" data-id="element-2392">
                    <UserIcon className="w-5 h-5 text-gray-400 mt-0.5" data-id="element-2393" />
                    <div data-id="element-2394">
                      <p className="text-sm text-gray-500" data-id="element-2395">Account Holder</p>
                      <p className="font-medium text-gray-900" data-id="element-2396">
                        {selectedMeter.name}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-2397">
                    <MapPinIcon className="w-5 h-5 text-gray-400 mt-0.5" data-id="element-2398" />
                    <div data-id="element-2399">
                      <p className="text-sm text-gray-500" data-id="element-2400">Service Address</p>
                      <p className="font-medium text-gray-900" data-id="element-2401">
                        {selectedMeter.address}
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-50" data-id="element-2402">
                    <div data-id="element-2403">
                      <p className="text-sm text-gray-500" data-id="element-2404">Tariff Type</p>
                      <p className="font-medium text-gray-900" data-id="element-2405">Residential</p>
                    </div>
                    <div data-id="element-2406">
                      <p className="text-sm text-gray-500" data-id="element-2407">Account No.</p>
                      <p className="font-medium text-gray-900 font-mono" data-id="element-2408">
                        ACC-{selectedMeter.id.slice(-4)}
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Live Reading */}
              <section className="text-white p-6 rounded-none shadow-inner bg-[#002F3E]" data-id="element-2409">
                <div className="flex justify-between items-start mb-4" data-id="element-2410">
                  <h3 className="text-sm font-medium text-teal-100 uppercase tracking-wider" data-id="element-2411">
                    Current Reading
                  </h3>
                  <div className="flex items-center gap-2 text-xs text-teal-200" data-id="element-2412">
                    {selectedMeter.status === 'Online' && <span className="w-2 h-2 bg-mavis-green rounded-none animate-pulse" data-id="element-2413"></span>}
                    Last updated: {selectedMeter.lastComm}
                  </div>
                </div>
                <div className="flex items-baseline gap-2 mb-4" data-id="element-2414">
                  <span className="text-4xl font-bold font-mono" data-id="element-2415">
                    {selectedMeter.reading.toFixed(1)}
                  </span>
                  <span className="text-teal-200" data-id="element-2416">kWh</span>
                </div>
                <div className="flex justify-between items-center pt-4 border-t border-white/10" data-id="element-2417">
                  <span className="text-sm text-teal-100" data-id="element-2418">
                    Today's Consumption
                  </span>
                  <span className="font-mono font-bold" data-id="element-2419">
                    {selectedMeter.daily.toFixed(1)} kWh
                  </span>
                </div>
              </section>

              {/* Chart */}
              <section data-id="element-2420">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4 border-b border-gray-100 pb-2" data-id="element-2421">
                  7-Day History
                </h3>
                <div className="h-48 w-full" data-id="element-2422">
                  <ResponsiveContainer width="100%" height="100%" data-id="element-2423">
                    <AreaChart data={selectedMeter.history.map((val, i) => ({
                  day: daysOfWeek[i],
                  value: val
                }))} margin={{
                  top: 10,
                  right: 0,
                  left: -20,
                  bottom: 0
                }} data-id="element-2424">

                      <defs data-id="element-2425">
                        <linearGradient id="colorMeterHistory" x1="0" y1="0" x2="0" y2="1" data-id="element-2426">

                          <stop offset="5%" stopColor="#0D4F5C" stopOpacity={0.3} data-id="element-2427" />


                          <stop offset="95%" stopColor="#0D4F5C" stopOpacity={0} data-id="element-2428" />

                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" data-id="element-2429" />


                      <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{
                    fill: '#6b7280',
                    fontSize: 12
                  }} dy={10} data-id="element-2430" />


                      <YAxis axisLine={false} tickLine={false} tick={{
                    fill: '#6b7280',
                    fontSize: 12
                  }} data-id="element-2431" />


                      <Tooltip formatter={(value: number) => [`${value} kWh`, 'Consumption']} contentStyle={{
                    borderRadius: '0',
                    border: 'none',
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                  }} data-id="element-2432" />


                      <Area type="monotone" dataKey="value" stroke="#0D4F5C" strokeWidth={2} fillOpacity={1} fill="url(#colorMeterHistory)" data-id="element-2433" />

                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </section>

              {/* Alert History */}
              <section data-id="element-2434">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4 border-b border-gray-100 pb-2" data-id="element-2435">
                  Recent Activity
                </h3>
                <div className="space-y-4 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-slate-300 before:to-transparent" data-id="element-2436">
                  <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-2437">
                    <div className="flex items-center justify-center w-10 h-10 rounded-none border border-white bg-orange-100 text-mavis-orange shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10" data-id="element-2438">
                      <AlertTriangleIcon className="w-5 h-5" data-id="element-2439" />
                    </div>
                    <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-4 rounded-none border border-gray-200 bg-white shadow-sm" data-id="element-2440">
                      <div className="flex items-center justify-between mb-1" data-id="element-2441">
                        <span className="font-bold text-gray-900 text-sm" data-id="element-2442">
                          High consumption detected
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 flex items-center gap-1" data-id="element-2443">
                        <ClockIcon className="w-3 h-3" data-id="element-2444" /> 2 hours ago
                      </div>
                    </div>
                  </div>

                  <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-2445">
                    <div className="flex items-center justify-center w-10 h-10 rounded-none border border-white bg-blue-100 text-mavis-blue shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10" data-id="element-2446">
                      <InfoIcon className="w-5 h-5" data-id="element-2447" />
                    </div>
                    <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-4 rounded-none border border-gray-200 bg-white shadow-sm" data-id="element-2448">
                      <div className="flex items-center justify-between mb-1" data-id="element-2449">
                        <span className="font-bold text-gray-900 text-sm" data-id="element-2450">
                          Meter reading synchronized
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 flex items-center gap-1" data-id="element-2451">
                        <ClockIcon className="w-3 h-3" data-id="element-2452" /> 6 hours ago
                      </div>
                    </div>
                  </div>

                  <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-2453">
                    <div className="flex items-center justify-center w-10 h-10 rounded-none border border-white bg-blue-100 text-mavis-blue shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10" data-id="element-2454">
                      <InfoIcon className="w-5 h-5" data-id="element-2455" />
                    </div>
                    <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-4 rounded-none border border-gray-200 bg-white shadow-sm" data-id="element-2456">
                      <div className="flex items-center justify-between mb-1" data-id="element-2457">
                        <span className="font-bold text-gray-900 text-sm" data-id="element-2458">
                          Token purchased - R200
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 flex items-center gap-1" data-id="element-2459">
                        <ClockIcon className="w-3 h-3" data-id="element-2460" /> 1 day ago
                      </div>
                    </div>
                  </div>

                  <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-2461">
                    <div className="flex items-center justify-center w-10 h-10 rounded-none border border-white bg-green-100 text-mavis-green shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10" data-id="element-2462">
                      <CheckCircle2Icon className="w-5 h-5" data-id="element-2463" />
                    </div>
                    <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-4 rounded-none border border-gray-200 bg-white shadow-sm" data-id="element-2464">
                      <div className="flex items-center justify-between mb-1" data-id="element-2465">
                        <span className="font-bold text-gray-900 text-sm" data-id="element-2466">
                          Tamper alert cleared
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 flex items-center gap-1" data-id="element-2467">
                        <ClockIcon className="w-3 h-3" data-id="element-2468" /> 3 days ago
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            </div>

            {/* Quick Actions Footer */}
            <div className="p-6 border-t border-gray-100 bg-gray-50 grid grid-cols-1 gap-3" data-id="element-2469">
              <button className="w-full py-3 bg-mavis-teal text-white font-medium rounded-none hover:bg-mavis-teal/90 transition-colors flex items-center justify-center gap-2 shadow-sm" data-id="element-2470">
                <ZapIcon className="w-4 h-4" data-id="element-2471" /> Generate Token
              </button>
              <div className="grid grid-cols-2 gap-3" data-id="element-2472">
                <button className="w-full py-2 bg-white border border-gray-300 text-gray-700 font-medium rounded-none hover:bg-gray-50 transition-colors text-sm shadow-sm" data-id="element-2473">
                  View History
                </button>
                <button className="w-full py-2 bg-white border border-gray-300 text-mavis-orange font-medium rounded-none hover:bg-orange-50 transition-colors text-sm shadow-sm" data-id="element-2474">
                  Report Issue
                </button>
              </div>
            </div>
          </div>}
      </div>
    </div>;
}